import React, { useEffect, useState, useRef, memo, ChangeEvent, useMemo, useCallback } from 'react'
import L from 'leaflet'
import '@geoman-io/leaflet-geoman-free'
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css'
import 'leaflet-path-drag'
import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import {
    Box,
    Typography,
    Paper,
    FormControl,
    FormControlLabel,
    FormGroup,
    Slider,
    IconButton,
    LinearProgress
} from '@mui/material'
import { v4 as uuidv4 } from 'uuid'

import { Restercoods } from '../../../utils/rastercoods'
import { get, post } from '../../../utils/http'
import {
    Version,
    ImageRes,
    ImageData,
    GeoJSON,
    ColorLibObj,
    Marker,
    Category
} from '../../../utils/types'
import {
    getSysAnnoData,
    getModifiedGSData,
    mergePolyList,
    processXMLMarkers,
    getXmlFile,
    drawHeatMap,
    processBcrHeatData,
    createGeojsonDataFromMarkerList
} from '../helper/processAnnotations'
import { MapWarningTexts } from '../helper/uiElements'
import { cachedFallbackGetLayer, cachedFallbackPostLayer, getMapProps } from '../helper/mapProps'
import { outputS3 } from '../../../utils/config'
import XmlList from '../helper/xmlList'
import { ViewerContext } from '../helper/context'

interface Props {
    image: ImageData,
    colorLib: ColorLibObj[]
    outputFiles: string[]
    refreshXmlList: Function
}

let mapContainer: any = null
const pubOutputS3Bucket: string = outputS3 || ''
const defaultMarkerColor = '#6b6bff'
let heatMapLayer: any = null
let tileCache: any = null

const Viewer = memo(({
    image,
    colorLib,
    outputFiles,
    refreshXmlList
}: Props) => {
    const mapRef = useRef<any>(null)
    const ongoingXmlReq = useRef(new Set())
    const xmlPolygonData = useRef<{[x: string]: GeoJSON}>({})

    const [mapCoords, setMapCoords] = useState<any>(null)
    const [brightnessValue, setBrightnessValue] = useState<number>(100)
    const [contrastValue, setContrastValue] = useState<number>(100)
    const [imageRes, setImgRes] = useState<ImageRes>({w: 0, h: 0, z: 0})
    const [showSlideFilters, setShowSlideFilters] = useState<boolean>(false)
    const [sysAnnotations, setSysAnnotations] = useState<Marker[]>([])
    const [annotations, setAnnotations] = useState<GeoJSON | {}>({})
    const [heatmap, setHeatmap] = useState<GeoJSON | {}>({})
    const [tissue, setTissue] = useState<GeoJSON | {}>({})
    const [versions, setVersions] = useState<Version[]>([]) // eslint-disable-line
    const [currentVersion, setCurrentVersion] = useState<number>(0)
    const [slideMenu, setSlideMenu] = useState<boolean>(false)
    const [mapError, setMapError] = useState<boolean>(false)
    const [renderType, setRenderType] = useState<string | null>(null)
    const [heatMapOverlay, setHeatMapOverlay] = useState<any>(null)
    const [isMarkersLoading, setIsMarkersLoading] = useState<boolean>(false)
    const [selectedItem, setSelectedItem] = useState<string>('annotation')
    const [xmlNames, setXmlNames] = useState<string[]>([])
    const [xmlClassFilter, setXmlClassFilter] = useState<{[x: string]: string[]}>({})
    const [xmlClasses, setXmlClasses] = useState<{ [x: string]: Category[] }>({})

    const cleanUp = () => {
        mapContainer = null
        heatMapLayer = null
        if (mapRef.current) {
            mapRef.current.remove()
            mapRef.current = null
        }
        if (tileCache) tileCache.clear()
    }

    const initMap = async () => {
        try {
            if (!mapRef.current) return

            const { maxZoom, url, slide_width, slide_height, customTileData } = await getMapProps(image)
            setImgRes({ w: slide_width, h: slide_height, z: maxZoom })

            const map = await L.map(mapRef.current, { 
                crc: L.CRS.Simple,
                minZoom: 0,
                maxZoom: maxZoom,
                preferCanvas: true,
                zoomSnap: 0,
                zoomDelta: 0.25,
                wheelPxPerZoomLevel: 90,
                zoomAnimation: true,
                zoomAnimationThreshold: 4,
                fadeAnimation: false,
                markerZoomAnimation: false,
                inertia: true,
                inertiaDeceleration: 3000,
                inertiaMaxSpeed: 1500
            })
            const tempMapCoords = new Restercoods(map, [slide_width, slide_height])
            tempMapCoords.setMaxBounds()
            setMapCoords(tempMapCoords)

            //----------Map outline (debug)--------------------
            // const coordinates = [{ X: 0, Y: 0}, { X: slide_width, Y: 0}, { X: slide_width, Y: slide_height}, { X: 0, Y: slide_height}]
            // const points = coordinates.map(vert => tempMapCoords.unproject([vert.X, vert.Y]))
            // L.polygon([points], { color: 'red', draggable: false, fillColor: "none" }).addTo(map)
            //------------------------------------------

            let tileLayer
            if (customTileData) {
                tileLayer = cachedFallbackPostLayer(customTileData, maxZoom)
            } else {
                ({ tileLayer, tileCache } = cachedFallbackGetLayer(url, maxZoom))
            }
            if (!tileLayer) throw Error('Failed to create tile layer')
            tileLayer.addTo(map)

            map.setView([0, 0], 2)
            map.setMaxBounds(null) // Able to drag the map freely without boundary lock.
            const options = {
                position: 'topleft',
                drawMarker: false,
                drawRectangle: false,
                drawPolygon: false,
                drawText: false,
                drawCircle: false,
                cutPolygon: false,
                editMode: false,
                dragMode: false,
                removalMode: false,
                drawCircleMarker: false,
                rotateMode: false,
                drawPolyline: false
            }
            map.pm.addControls(options)
            // Brightness, contrast sliders
            map.pm.Toolbar.createCustomControl({
                name: 'filter',
                block: 'custom',
                title: 'Filter',
                className: 'icon-filters',
                onClick: () => setShowSlideFilters(prevState => !prevState)
            })
            mapContainer = map
        } catch (err) {
            console.log('Map error')
            setMapError(true)
        }
    }

    const createHeatmap = (geojson: GeoJSON) => {
        try {
            if (heatMapOverlay) heatMapLayer = heatMapOverlay.addTo(mapContainer)
            else {
                const heatMapPoints = processBcrHeatData(geojson, imageRes.w, imageRes.h)
                heatMapLayer = drawHeatMap(document.getElementById(`heatmap-container-${image.id}`)!, heatMapPoints, mapCoords, imageRes.w, imageRes.h, 1)
                heatMapLayer.addTo(mapContainer)
                setHeatMapOverlay(heatMapLayer)
            }
        } catch (err) {
          console.log(err)
        }
    }

    const processHeatmap = async () => {
        setIsMarkersLoading(true)
        try {
            const res = await post('S3/bcr-heatmap-file', {
                s3_name: pubOutputS3Bucket,
                directory: image.image_unique_id,
                file_type: image.file_type || 'svs'
            })
            if ('key' in res && res.key.length) {
                const { xml, size } = await getXmlFile(res.key, pubOutputS3Bucket, null)    // eslint-disable-line
                const { geoJsonData, categories } = processXMLMarkers(xml, false, mapCoords, colorLib)  // eslint-disable-line
                setHeatmap(geoJsonData)
                setRenderType(`heatmap-${new Date()}`)
            } else throw Error('file not found')
        } catch (err) {
            console.error(err)
        } finally {
            setIsMarkersLoading(false)
        }
    }

    const processTissue = async () => {
        setIsMarkersLoading(true)
        try {
            const res = await post('S3/tissue-file', {
                s3_name: pubOutputS3Bucket,
                directory: image.image_unique_id,
                file_type: image.file_type  || 'svs'
            })
            if ('key' in res && res.key.length) {
                const { xml, size } = await getXmlFile(res.key, pubOutputS3Bucket, null)    // eslint-disable-line
                const { geoJsonData, categories } = processXMLMarkers(xml, false, mapCoords, colorLib)  // eslint-disable-line
                setTissue(geoJsonData)
                setRenderType(`tissue-${new Date()}`)
            } else throw Error('file not found')
        } catch (err) {
            console.log(err)
        } finally {
            setIsMarkersLoading(false)
        }
    }

    const clearMapLayers = () => {
        if (mapContainer) {
            if (heatMapLayer) mapContainer.removeLayer(heatMapLayer)
            mapContainer.eachLayer((layer: any) => {
                if (layer instanceof L.Path && 'properties' in layer) mapContainer.removeLayer(layer)
                else if (layer instanceof L.GeoJSON) mapContainer.removeLayer(layer)
            })
        }
    }

    const renderGeojsonPolygon = (geojsonData: GeoJSON, filter: string[], filterReverse: boolean = false) => {
        try {
            const fixedFilter = filter.map((i) => i && i.toLowerCase()).filter((i) => i && i.length);
            return L.geoJSON(geojsonData, {
                style: (feature: any) => ({color: feature.properties.color || defaultMarkerColor}),
                filter: (feature: any) => fixedFilter.length
                    ? filterReverse
                        ? fixedFilter.includes(feature.properties.category.toLowerCase())
                        : !fixedFilter.includes(feature.properties.category.toLowerCase())
                    : true,
                onEachFeature: (feature: any, layer: any) => {
                    layer.bindTooltip(`${feature.properties.category}`, {className: "marker-popup"});
                },
                pointToLayer: (feature: any, latlng: any) => L.circleMarker(latlng, {
                    radius: 7,
                    color: "blue",
                    fillOpacity: 0.2,
                }),
            }).addTo(mapContainer);
        } catch (err) {
            console.error(err);
        }
    }

    const renderXMLAnnotations = () => {
        try {
            xmlNames.forEach(name => {
                if (name.length && name in xmlPolygonData.current) {
                    const filter = name in xmlClassFilter ? xmlClassFilter[name] : []
                    renderGeojsonPolygon(xmlPolygonData.current[name], filter)
                }
            })
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        clearMapLayers()
        if (renderType && mapCoords) {
            if (renderType.includes('annotation')) {
                if ('features' in annotations) renderGeojsonPolygon(annotations, [])
            } else if (renderType.includes('heatmap')) {
                if ('features' in heatmap) createHeatmap(heatmap)
                else processHeatmap()
            } else if (renderType.includes('tissue')) {
                if ('features' in tissue) renderGeojsonPolygon(tissue, [])
                else processTissue()
            } else if (renderType.includes('xml')) {
                // clear prediction check
                setSelectedItem('')
                renderXMLAnnotations()
            }

            if (!renderType.includes('xml')) {
                // clear xml checks
                setXmlClassFilter({})
                setXmlNames([])
            }
        }
    }, [renderType, mapCoords])    // eslint-disable-line

    const fetchAnnotations = async () => {
        setIsMarkersLoading(true)
        try {
            const res = await getModifiedGSData(currentVersion)
            if (!res) throw Error('Annotation data not found')
            if (sysAnnotations.length) {
                const fixedAnnotation = mergePolyList(sysAnnotations, res.custom_markers)
                const geojsonData = createGeojsonDataFromMarkerList(fixedAnnotation, colorLib, mapCoords)
                setAnnotations(geojsonData)
            } else {
                const res2 = await getSysAnnoData(res.sys_marker_ver, null)
                const fixedAnnotation = mergePolyList(res2.markers, res.custom_markers)
                const geojsonData = createGeojsonDataFromMarkerList(fixedAnnotation, colorLib, mapCoords)
                setAnnotations(geojsonData)
                setSysAnnotations(res2.markers)
            }
            setRenderType(`annotation-${new Date()}`)
        } catch (err) {
            console.log(err)
        } finally {
            setIsMarkersLoading(false)
        }
    }

    useEffect(() => {
        if (currentVersion && mapCoords) fetchAnnotations()
        else console.error('map container not found')
    }, [currentVersion, mapCoords])    // eslint-disable-line

    const getVersionInfo = async () => {
        // Slides in master-dataset directory (internal viewer) will show version from all users
        if (image.input_s3 && image.input_s3 === 'master-dataset') {
            const res = await get(`Annotations/versions-by-type/${image.id}`)
            if (!res.length) return
            setVersions(res)
            handleVersionChange(res[0].id)
        } else {
            // Slides in user directory (My Folder/Mounts/Shared) will only show
            // versions from their own and the users the slide shared with
            const userIds: number[] = [...new Set([...image.uploaded_by, ...image.shared])]
            const res = await post(`Annotations/versions-by-type/${image.id}`, {user_ids: userIds})
            if (!res.length) return
            setVersions(res)
            handleVersionChange(res[0].id)
        }
    }

    useEffect(() => {
        initMap()
        getVersionInfo()
        return () => cleanUp()
    }, [image.id])   // eslint-disable-line

    const handleVersionChange = (ver: number) => setCurrentVersion(ver)

    useEffect(() => {
        if (mapRef.current) {
            mapRef.current.children.forEach((i: any) => {
                if (i.className !== 'leaflet-control-container') i.style.filter = `brightness(${brightnessValue}%) contrast(${contrastValue}%)`
            })
        }
    }, [brightnessValue, contrastValue])

    const adjustBrightness = (e: any) => setBrightnessValue(Number(e.target.value))
    const adjustContrast = (e: any) => setContrastValue(Number(e.target.value))

    const handleRender = (e: ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedItem(e.target.value)
            setRenderType(e.target.value)
        } else {
            setSelectedItem('')
            setRenderType(null)
        }
    }

    useEffect(() => {
        if (Object.keys(xmlClassFilter).length) setRenderType(`xml-${uuidv4()}`)
    }, [xmlClassFilter])  // eslint-disable-line

    const fetchXMLData = async (signal = null) => {
        setIsMarkersLoading(true)
        try {
            const promises = xmlNames.map(async (name) => {
                if (name.length && !(name in xmlPolygonData.current) && !ongoingXmlReq.current.has(name)) {
                    ongoingXmlReq.current.add(name);
                    try {
                        const { xml, size } = await getXmlFile(name.split("/").slice(1).join("/"), name.split("/")[0], signal); // eslint-disable-line
                        const { geoJsonData, categories } = processXMLMarkers(xml, false, mapCoords, colorLib);
                        xmlPolygonData.current[name] = geoJsonData
                        setXmlClasses((prev) => ({ ...prev, [name]: categories }));
                    } catch (err) {
                        console.log(err);
                        setXmlClasses((prev) => ({ ...prev, [name]: [] }));
                    } finally {
                        ongoingXmlReq.current.delete(name);
                    }
                }
            })
            await Promise.all(promises).then(() => setRenderType(`xml-${uuidv4()}`))
        } catch (err) {
            console.log(err);
        } finally {
            setIsMarkersLoading(false);
        }
    };
    
    useEffect(() => {
        if (xmlNames.length) fetchXMLData()
    }, [xmlNames])  // eslint-disable-line

    const removeXMLData = useCallback((fileName: string) => {
        // Reset class filter without re-render for this xml when closing
        setXmlClassFilter((prevState: {[x: string]: string[]}) => {
            prevState[fileName] = []
            return prevState
        })
        setXmlNames((prevState: string[]) => {
            const list = prevState.filter(i => i && i !== fileName)
            if (!list.length) list.push('')
            return list
        })
    }, [])  // eslint-disable-line
    
    const renderXMLData = useCallback((fileName: string) => {
        // Reset class filter without re-render for this xml when closing
        setXmlClassFilter((prevState: {[x: string]: string[]}) => {
            prevState[fileName] = []
            return prevState
        })
        setXmlNames((prevState: string[]) => {
            if (prevState.includes(fileName)) return prevState
            return [...prevState, fileName]
        })
    }, [])  // eslint-disable-line
    
    const removeXMLClassData = useCallback((fileName: string, className: string) => {
        setXmlClassFilter((prevState: {[x: string]: string[]}) => {
            if (fileName in prevState) {
                const temp = prevState[fileName].filter(i => i && i!==className)
                return {...prevState, [fileName]: temp}
            } else return prevState
        })
    }, [])  // eslint-disable-line
    
    const addXMLClassData = useCallback((fileName: string, className: string) => {
        setXmlClassFilter((prevState: {[x: string]: string[]}) => {
            if (!(fileName in prevState)) prevState[fileName] = []
            const temp = prevState[fileName]
            if (!prevState[fileName].includes(className)) temp.push(className)
            return {...prevState, [fileName]: temp}
        })
    }, [])  // eslint-disable-line

    const MemoizedXmlList = useMemo(() =>
        <XmlList
            imageData={image}
            list={outputFiles}
            refreshList={refreshXmlList}
            renderXMLData={renderXMLData}
            removeXMLData={removeXMLData}
            addXMLClassData={addXMLClassData}
            removeXMLClassData={removeXMLClassData}
        />
    , [image, outputFiles])  // eslint-disable-line

    const resetMap = () => {}
    const toggleLoading = () => {}

    return (
        <ViewerContext.Provider value={{
            mapCoords, colorLib, xmlClasses, jsonClasses: {}, xmlNames, xmlClassFilter, jsonName: '', jsonClassFilter: '', resetMap, 
            sysClassFilter: [], userFilter: [], BCRScore: 0, imageWidth: 0, imageHeight: 0, imageZoomLvl: 0, bcrClassFilter: [],
            toggleLoading
        }}>
        {mapError
            ? <MapWarningTexts text={"Viewer failed to initiate, please try again later"} />
            : <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flexGrow: 1 }}>
                <Box sx={{
                    position: "absolute",
                    top: 16,
                    alignSelf: "center",
                    zIndex: 999,
                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                    backdropFilter: "blur(8px)",
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
                    borderRadius: "12px",
                    px: 2,
                    pt: 0.5,
                    pb: 0.25,
                }}>
                    <Typography 
                        component="h1" 
                        variant="caption"
                        sx={{
                            display: "block",
                            maxWidth: "40vw", 
                            color: "text.primary",
                            fontWeight: 400,
                            letterSpacing: "0.5px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis" 
                        }}
                    >
                        {image.name.split("/").pop()}
                    </Typography>
                </Box>

                <div key={'map-' + image.id} ref={mapRef} className="map-style"></div>
                <div id={`heatmap-container-${image.id}`} style={{ position: 'absolute', display: 'none' }}></div>

                <Box sx={{ position: 'absolute', top: '10px', right: '10px', zIndex: 999 }}>
                    <IconButton 
                        onClick={() => setSlideMenu(prevState => !prevState)}
                        sx={{
                            width: 'fit-content',
                            minWidth: 'unset',
                            borderRadius: 2,
                            backgroundColor: "rgba(255, 255, 255, 0.9)",
                            backdropFilter: "blur(8px)",
                            border: '2px solid #ccc'
                        }}
                        title="open slide menu"
                    >
                        {slideMenu ? <ChevronRight /> : <ChevronLeft />}
                    </IconButton>
                    <Paper elevation={3} sx={{
                        position: 'absolute',
                        right: 0,
                        display: slideMenu ? 'block' : 'none',
                        width: 340,
                        maxHeight: 'calc(100vh - 110px)',
                        overflow: 'auto'
                    }}>
                        <FormControl onChange={handleRender} className='px-3 py-2'>
                            <FormGroup defaultValue="annotation" className='p-3 gap-2'>
                                <FormControlLabel 
                                    value="annotation"
                                    className='cursor-pointer'
                                    control={
                                        <input
                                            className='form-check-input m-0 shadow-none me-2'
                                            type='checkbox'
                                            checked={selectedItem==='annotation'}
                                            readOnly
                                        />
                                    }
                                    label="System Annotations"
                                />
                                <FormControlLabel 
                                    value="heatmap"
                                    className='cursor-pointer'
                                    control={
                                        <input 
                                            className='form-check-input m-0 shadow-none me-2' 
                                            type='checkbox'
                                            checked={selectedItem==='heatmap'}
                                            readOnly
                                        />
                                    }
                                    label="BCR Heatmap"
                                />
                                <FormControlLabel 
                                    value="tissue"
                                    className='cursor-pointer'
                                    control={
                                        <input 
                                            className='form-check-input m-0 shadow-none me-2' 
                                            type='checkbox' 
                                            checked={selectedItem==='tissue'}
                                            readOnly
                                        />
                                    }
                                    label="Tissue Outline"
                                />
                            </FormGroup>
                        </FormControl>
                        <hr className='m-0' />
                        <div className='py-3 px-4'>{MemoizedXmlList}</div>
                    </Paper>
                </Box>

                {/* Filter popup */}
                <Box sx={{ position: "absolute", bottom: "5rem", display: showSlideFilters ? 'flex' : 'none', zIndex: 999, alignSelf: 'center'}}>
                  <Paper sx={{ display: 'flex', flexDirection: 'column', width: 300, gap: '1rem', padding: '2rem' }} elevation={3}>
                    <Box>
                      <Typography gutterBottom>Brightness</Typography>
                      <Slider 
                        value={brightnessValue}
                        step={10}
                        min={0}
                        max={200} 
                        marks 
                        aria-label="brightness slider" 
                        onChange={adjustBrightness}
                      />
                    </Box>
                    <Box>
                      <Typography gutterBottom>Contrast</Typography>
                      <Slider 
                        value={contrastValue}
                        step={10}
                        min={0}
                        max={200} 
                        marks
                        aria-label="contrast slider"
                        onChange={adjustContrast}
                      />
                    </Box>
                  </Paper>
                </Box>

                {isMarkersLoading &&
                    <Box className='position-absolute w-100' sx={{bottom: 0, left: 0}}>
                        <LinearProgress/>
                    </Box>
                }
            </Box>
        }
        </ViewerContext.Provider>
    )
})

export default Viewer